import React, {createContext, useContext, useImperativeHandle} from 'react';
import {
	useAttributeSetSelection,
	type UseAttributeSetSelectionParams,
} from '../hooks/useAttributeSetSelection';

export type AttributeSetSelectionContextType = ReturnType<
	typeof useAttributeSetSelection
>;

const AttributeSetSelectionContext =
	createContext<AttributeSetSelectionContextType>({
		allAttributeTemplates: [],
		attributeTemplates: {},
		attributeTemplateIds: [],
		attributesWorking: {},
		enforce: [],
		loadedSets: [],
		loadingSets: [],
		loadingTemplates: false,
		lookupsAllowed: {
			targets: [],
			keys: [],
		},
		selected: [],
		lookup: async () => ({}),
		setSelected: async () => {},
		setConfirm: () => {},
		setOrder: () => {},
		attributeSetCtx: {
			allAttributeTemplatesAsObject: {},
			allAttributeIds: [],
			allAttributeTemplates: [],
			attributesCtx: {
				data: [],
				attributeIds: [],
				attributesWorking: {},
				attributesTrackingHistory: [],
				trackingHistory: false,
				hasMandatoryUserDefinedTemplates: false,
				loading: false,
				metadataLookupsEnabled: false,
				searchValue: () => undefined,
				total: 0,
				lookup: async () => ({}),
				getById: () => undefined,
				lookups: {
					keys: [],
					targets: [],
				},
				setPermissionEdit: () => {},
				getAll: () => [],
				getAllAsObject: () => ({}),
				getByIds: () => [],
				getByIdsAsObject: () => ({}),
				getByName: () => undefined,
				getByNames: () => [],
				getByType: () => [],
				getDefaultValues: () => [],
				getLabelById: () => '',
			},
			totalAttributeTemplates: 0,
			data: [],
			loading: false,
			loadingTemplates: false,
			getAttributeTemplatesById: async () => [],
			getAttributeTemplatesByIds: async () => [],
			getAttributeTemplateIdsById: async () => [],
			getAttributeTemplateIdsByIds: async () => [],
			getById: () => undefined,
			getByIds: () => [],
			getFilterById: () => undefined,
			getMetadataLookupsById: async () => ({
				keys: [],
				targets: [],
			}),
			getMetadataLookupsByIds: async () => ({
				keys: [],
				targets: [],
			}),
			getSelectable: () => [],
			lookup: async () => ({}),
		},
	});

export const useAttributeSetSelectionProvider = () =>
	useContext(AttributeSetSelectionContext);

export type AttributeSetSelectionProviderRef =
	| React.RefObject<AttributeSetSelectionContextType>
	| ((node: AttributeSetSelectionContextType) => void)
	| null;

export type AttributeSetSelectionProviderChildrenProps =
	AttributeSetSelectionContextType;

export type AttributeSetSelectionProviderProps =
	UseAttributeSetSelectionParams & {
		ref?: AttributeSetSelectionProviderRef;
		children:
			| React.ReactNode
			| ((
					props: AttributeSetSelectionProviderChildrenProps,
			  ) => React.ReactNode);
	};

const AttributeSetSelectionProviderInner = (
	{children, ...props}: AttributeSetSelectionProviderProps,
	ref: AttributeSetSelectionProviderRef,
) => {
	const attributeSetSelection = useAttributeSetSelection(props);

	useImperativeHandle(ref, () => attributeSetSelection, [
		attributeSetSelection,
	]);

	return (
		<AttributeSetSelectionContext.Provider value={attributeSetSelection}>
			{typeof children === 'function'
				? children(attributeSetSelection)
				: children}
		</AttributeSetSelectionContext.Provider>
	);
};

export const AttributeSetSelectionProviderPrimitive = React.forwardRef<
	AttributeSetSelectionContextType,
	AttributeSetSelectionProviderProps
>(AttributeSetSelectionProviderInner);

/**
 * Attribute set context provider
 * @example
 * <AttributeSetSelectionProvider ref={attributeSesRef}>
 * 	...
 * </AttributeSetSelectionProvider>
 * @example
 * <AttributeSetSelectionProvider ref={attributeSetSelectionRef}>
 * 	{(attributeSetSelection) => (
 * 		...
 * 	)}
 * </AttributeSetSelectionProvider>
 */
export const AttributeSetSelectionProvider = React.memo(
	AttributeSetSelectionProviderPrimitive,
);

export default AttributeSetSelectionProvider;
